<template>
  <fs-tooltip
    tag="label"
    :text="modelValue"
    :show="Boolean(modelValue)"
    :class="{
      'fs-input-text': true,
      'fs-input-text--edit-hint': props.editHint,
      'fs-input-text--large': props.size === 'large',
    }"
    :data-value="modelValue ?? ''"
  >
    <fs-input-text
      ref="input"
      v-model="modelValue"
      class="fs-input-text__input"
      :maxlength="props.maxlength"
      :placeholder="props.placeholder"
      :reset-on-empty="props.resetOnEmpty"
      size="1"
      @focus="emit('focus')"
      @blur="emit('blur')"
      @save="(value) => emit('save', value)"
    />
    <span v-if="props.editHint" class="fs-input-text__edit-icon" v-html="pencilIcon"></span>
  </fs-tooltip>
</template>

<script setup lang="ts">
import FsTooltip from '@components/poppers/fs-tooltip.vue';
import FsInputText from '@components/form-elements/fs-input-text.vue';
import pencilIcon from '@icons/other/pencil-icon.svg';
import { ref } from 'vue';

/**
 * Standardized input that looks like normal text until hovered or focused.
 * The actual text style should be set in the parent component, but this component
 * handles the border & background as well as allowing it to grow up until the CSS max-width.
 */

const modelValue = defineModel<string>();

const emit = defineEmits<{ focus: []; blur: []; save: [value?: string] }>();
const props = defineProps<{
  placeholder?: string;
  maxlength?: number;
  resetOnEmpty?: boolean;
  editHint?: boolean;
  // Large opens into a box like a cell's value input
  size?: 'large';
}>();

const input = ref<InstanceType<typeof FsInputText> | null>(null);

defineExpose({
  focus: () => input.value?.focus(),
  blur: () => input.value?.blur(),
});
</script>

<style scoped lang="scss">
@import '@styles/variables';

.fs-input-text {
  position: relative;
  display: inline-grid;

  // Ensure too long text is truncated
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  // Styling of the input
  padding: 0.1rem 0.2rem;
  cursor: text;
  outline: none;
  border: 1px rgba($c-dark, 0) solid;
  border-radius: 0.5rem;
  color: $c-dark;
  transition:
    border-color 0.1s ease,
    background-color 0.1s ease;

  &::placeholder {
    color: rgba($c-dark, 0.5);
  }

  &--edit-hint {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
  }

  &--edit-hint &__input::placeholder {
    font-style: italic;
    color: rgba($c-dark, 0.7);
  }

  &__edit-icon {
    grid-area: 1 / 2;
    width: 1.2rem;
    height: 1.2rem;
    min-width: 1.2rem;
    margin-left: 0.6rem;
    opacity: 0;
    transition: opacity 0.1s ease;

    :deep(svg) {
      width: 100%;
      height: 100%;
      fill: rgba($c-dark, 0.7);
    }
  }

  &[data-value=''] &__edit-icon,
  &:hover &__edit-icon,
  &:focus-within &__edit-icon {
    opacity: 1;
  }

  &:hover {
    border-color: rgb($c-dark, 0.2);
  }

  &:focus-within {
    border-color: rgba($c-dark, 0.5);
    background: $c-bg-light;
  }

  &--large {
    padding: 0.4rem 0.8rem;
    border-radius: 0.8rem;

    &:focus-within {
      border-color: $c-dark;
      background: #fff;
    }
  }

  &::after,
  &__input {
    // Hack to inherit all properties added to the label.
    all: inherit;

    // Reset all properties that shouldn't be inherited
    margin: 0;
    padding: 0;
    border: none;
    display: inline-block;
    box-sizing: border-box;
    width: auto;
    grid-area: 1 / 1;
  }

  // This makes the label as wide as the input (unless max-width prevents that)
  // Inspired by https://css-tricks.com/auto-growing-inputs-textareas/#aa-other-ideas
  &::after {
    content: attr(data-value) ' ';
    visibility: hidden;
    white-space: pre;
  }
}
</style>
